import _ from 'lodash'
import {ReportableError} from '@wix/document-manager-utils'
import {getIdFromRef} from '../utils/dataUtils'
import type {DataModelExtensionAPI} from './dataModel/dataModel'
import {DATA_TYPES, MASTER_PAGE_ID} from '../constants/constants'
import type {Pointer} from '@wix/document-services-types'
import {DmApis, Extension, ExtensionAPI, pointerUtils} from '@wix/document-manager-core'
import {type DataFixerVersioningApi, FixerCategory} from './dataFixerVersioning/dataFixerVersioning'

const EFFECTS = DATA_TYPES.effects
const {getPointer} = pointerUtils

const createExtension = (): Extension => {
    const createExtensionAPI = ({dal, extensionAPI, pointers}: DmApis): ExtensionAPI => {
        const {dataModel} = extensionAPI as DataModelExtensionAPI
        const {dataFixerVersioning} = extensionAPI as DataFixerVersioningApi

        const getAllEffectsPointers = (componentPointer: Pointer): Pointer[] => {
            const effectsListItem = dataModel.components.getItem(componentPointer, EFFECTS)
            return _.map(effectsListItem?.values, ({id}) => getPointer(getIdFromRef(id), EFFECTS))
        }

        const usesNewAnimations = (): boolean => {
            return Boolean(dal.get(pointers.data.getDataItemFromMaster(MASTER_PAGE_ID))?.usesNewAnimations)
        }

        const usesNewBgScrub = (): boolean => {
            return (
                usesNewAnimations() &&
                dataFixerVersioning.hasFixerRunOnCurrentVersion(
                    MASTER_PAGE_ID,
                    FixerCategory.VIEWER,
                    'migrateScreenInBehaviorsToEntranceEffects',
                    2,
                    {
                        fixers: {
                            migrateScreenInBehaviorsToEntranceEffects:
                                'MTA3X2Q3MjA3NmE5OGIzOGZlNDdkNTUyZjQwNzE1YjgxYzc2'
                        },
                        lastFixerIndex: -1
                    }
                )
            )
        }

        const createEffectsError = (message: string) => {
            return new ReportableError({
                message,
                errorType: 'EffectsError'
            })
        }

        return {
            effects: {
                usesNewAnimations,
                usesNewBgScrub,
                createEffectsError,
                list: getAllEffectsPointers
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis) => {
        const {effects} = extensionAPI as EffectsExtensionAPI
        return {
            effects: {
                usesNewBgScrub: effects.usesNewBgScrub
            }
        }
    }

    return {
        name: 'effects',
        dependencies: new Set(['dataModel', 'dataFixerVersioning']),
        createExtensionAPI,
        createPublicAPI
    }
}

export interface EffectsExtensionAPI extends ExtensionAPI {
    effects: {
        usesNewAnimations(): boolean
        usesNewBgScrub(): boolean
        list(componentPointer: Pointer): Pointer[]
    }
}

export {createExtension}
